'use client';
import React from 'react';
import Icon from '@/components/ui/AppIcon';

interface Delivery {
  id: string;
  pickupCity: string;
  destinationCity: string;
  routeProgress: number;
}

interface Breadcrumb {
  id: string;
  latitude: number | null;
  longitude: number | null;
  location_name: string;
  speed_kmh: number | null;
  created_at: string;
}

interface Props {
  delivery: Delivery;
  breadcrumbs: Breadcrumb[];
}

function fmtMinute(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleString('en-KE', {
    day: 'numeric', month: 'short',
    hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true,
  });
}

export default function BreadcrumbMap({ delivery, breadcrumbs }: Props) {
  const gpsPoints = breadcrumbs.filter(b => b.latitude !== null && b.longitude !== null);
  const textPoints = breadcrumbs.filter(b => b.latitude === null);

  return (
    <div className="card-elevated p-5 space-y-5">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-700 text-foreground">Geolocation Breadcrumbs</h3>
        <div className="flex items-center gap-3 text-xs text-muted-foreground">
          <span className="flex items-center gap-1">
            <span className="w-2 h-2 rounded-full bg-primary" />
            GPS ({gpsPoints.length})
          </span>
          <span className="flex items-center gap-1">
            <span className="w-2 h-2 rounded-full bg-accent" />
            Checkpoint ({textPoints.length})
          </span>
        </div>
      </div>

      {/* Route progress bar */}
      <div>
        <div className="flex justify-between text-xs text-muted-foreground mb-1.5">
          <span className="font-600 text-foreground">{delivery.pickupCity}</span>
          <span className="font-700 text-primary">{delivery.routeProgress}% complete</span>
          <span className="font-600 text-foreground">{delivery.destinationCity}</span>
        </div>
        <div className="relative h-3 bg-muted rounded-full overflow-hidden">
          <div
            className="h-full gradient-primary rounded-full transition-all duration-700"
            style={{ width: `${delivery.routeProgress}%` }}
          />
          {/* Traveler dot */}
          <div
            className="absolute top-1/2 -translate-y-1/2 w-3 h-3 bg-white border-2 border-primary rounded-full shadow-sm transition-all duration-700"
            style={{ left: `calc(${delivery.routeProgress}% - 6px)` }}
          />
        </div>
      </div>

      {breadcrumbs.length === 0 ? (
        <div className="text-center py-8">
          <Icon name="MapIcon" size={36} className="text-muted-foreground/30 mx-auto mb-2" />
          <p className="text-sm font-600 text-muted-foreground">No breadcrumbs yet</p>
          <p className="text-xs text-muted-foreground/70 mt-1">
            The traveler has not shared any location checkpoints or GPS pings yet.
          </p>
        </div>
      ) : (
        <div className="space-y-2 max-h-96 overflow-y-auto pr-1">
          {/* Latest first */}
          {[...breadcrumbs].reverse().map((b, idx) => {
            const isGps = b.latitude !== null;
            const isFirst = idx === 0;
            return (
              <div
                key={b.id}
                className={`flex items-start gap-3 p-3 rounded-xl border transition-colors ${
                  isFirst ? 'border-primary/30 bg-primary/5' : 'border-border bg-muted/20'
                }`}
              >
                {/* Icon */}
                <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 ${
                  isGps ? 'bg-primary/10' : 'bg-accent/10'
                }`}>
                  <Icon
                    name={isGps ? 'SignalIcon' : 'MapPinIcon'}
                    size={13}
                    className={isGps ? 'text-primary' : 'text-accent'}
                  />
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-xs font-600 text-foreground truncate">
                      {b.location_name || (isGps ? 'GPS Point' : 'Checkpoint')}
                    </p>
                    {isFirst && (
                      <span className="flex items-center gap-1 text-[10px] font-600 text-primary bg-primary/10 px-1.5 py-0.5 rounded-full shrink-0">
                        <span className="w-1 h-1 rounded-full bg-primary animate-pulse" />
                        Latest
                      </span>
                    )}
                  </div>

                  <p className="text-[10px] text-muted-foreground mt-0.5 tabular-nums">{fmtMinute(b.created_at)}</p>

                  {isGps && b.latitude !== null && b.longitude !== null && (
                    <div className="flex items-center gap-3 mt-1.5 flex-wrap">
                      <span className="text-[10px] font-500 text-primary bg-primary/5 px-2 py-0.5 rounded-md tabular-nums">
                        {b.latitude.toFixed(5)}, {b.longitude.toFixed(5)}
                      </span>
                      {b.speed_kmh !== null && (
                        <span className="flex items-center gap-1 text-[10px] text-muted-foreground">
                          <Icon name="BoltIcon" size={9} />
                          {b.speed_kmh.toFixed(1)} km/h
                        </span>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* GPS link hint */}
      {gpsPoints.length > 0 && (
        <div className="pt-3 border-t border-border">
          <p className="text-[10px] text-muted-foreground">
            <Icon name="InformationCircleIcon" size={11} className="inline mr-1" />
            {gpsPoints.length} GPS coordinate{gpsPoints.length !== 1 ? 's' : ''} recorded.
            Coordinates can be plotted on any mapping service using the lat/lng values above.
          </p>
        </div>
      )}
    </div>
  );
}
